import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { Badge } from './badge';
import { cn } from './cn';
import { localRect, localViewport } from '../../utils/zoom';

const MENU_MAX_HEIGHT = 280;
const GAP = 4;
const EDGE = 8;

/**
 * shadcn-shaped Select: trigger + portalled listbox with a check on the
 * selected item. The menu lives on <body> so a card's overflow never clips it.
 *
 * options: [{ value, label, badge?, separatorAfter? }] — `value` is compared
 * with ===, so '' works as a "none" entry. `badge` renders a small outline
 * Badge beside the label; `separatorAfter` closes off a leading group.
 * `label` heads the menu (SelectLabel).
 */
function Select({
  value,
  onValueChange,
  options = [],
  placeholder = 'Select…',
  label,
  size = 'default',
  align = 'start',
  ariaLabel,
  disabled = false,
  className,
  minMenuWidth = 160,
  renderValue,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const baseId = useMemo(() => `ui-sel-${Math.random().toString(36).slice(2, 9)}`, []);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = localRect(el);
    const vp = localViewport();
    const width = Math.max(r.width, minMenuWidth);
    const below = vp.height - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const flip = below < Math.min(MENU_MAX_HEIGHT, 140) && above > below;
    const left = align === 'end' ? r.right - width : r.left;
    setPos({
      left: Math.max(EDGE, Math.min(left, vp.width - width - EDGE)),
      top: flip ? undefined : r.bottom + GAP,
      bottom: flip ? vp.height - r.top + GAP : undefined,
      width,
      maxHeight: Math.min(MENU_MAX_HEIGHT, flip ? above : below),
    });
  }, [align, minMenuWidth]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0 || !menuRef.current) return;
    const node = menuRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const commit = (opt) => {
    setOpen(false);
    triggerRef.current?.focus();
    if (opt && opt.value !== value) onValueChange?.(opt.value);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(options.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActiveIndex(0); }
    else if (e.key === 'End') { e.preventDefault(); setActiveIndex(options.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); commit(options[activeIndex]); }
    else if (e.key === 'Tab') setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-controls={open ? `${baseId}-list` : undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className={cn('ui-select-trigger', size === 'sm' && 'ui-select-trigger--sm', className)}
      >
        <span className={cn('ui-select-trigger__value', !selected && 'ui-select-trigger__value--placeholder')}>
          {selected ? (renderValue ? renderValue(selected) : selected.label) : placeholder}
        </span>
        <ChevronDown aria-hidden />
      </button>

      {open && pos && ReactDOM.createPortal(
        <div
          ref={menuRef}
          id={`${baseId}-list`}
          role="listbox"
          aria-label={ariaLabel}
          className="ui-select-content"
          style={{
            left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width, maxHeight: pos.maxHeight,
          }}
        >
          {label && <div className="ui-select-label">{label}</div>}
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            return (
              <React.Fragment key={String(opt.value)}>
                <div
                  data-idx={i}
                  data-active={i === activeIndex ? 'true' : 'false'}
                  role="option"
                  aria-selected={isSelected}
                  className="ui-select-item"
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => commit(opt)}
                  title={opt.label}
                >
                  <span className="ui-select-item__text">{opt.label}</span>
                  {opt.badge && <Badge variant="outline">{opt.badge}</Badge>}
                  {isSelected && (
                    <span className="ui-select-item__check"><Check aria-hidden /></span>
                  )}
                </div>
                {opt.separatorAfter && <div className="ui-select-separator" role="separator" />}
              </React.Fragment>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

export { Select };
